import { useCallback, useState } from 'react';

/**
 * Per-viewer display preferences kept in localStorage (e.g. Cards | Compact).
 * Storage can be missing or throw (private mode, blocked site data), so every
 * access is guarded and the default is used instead.
 */
export function readPref<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const v = window.localStorage.getItem(key);
    return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writePref(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ponytail: storage unavailable - the choice lasts for this page view only
  }
}

/** A sticky preference: state that survives reloads for this viewer. */
export function useStickyPref<T extends string>(key: string, fallback: T, allowed: readonly T[]) {
  const [value, setValue] = useState<T>(() => readPref(key, fallback, allowed));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      writePref(key, next);
    },
    [key],
  );
  return [value, set] as const;
}

/** Preference keys used across the customer UI. */
export const PREF = {
  /** 'on' | 'off' - show which tips won above every meeting card's bars. */
  tipsWonOnCards: 'tipsWonOnCards',
  /** 'cards' | 'compact' | 'field' - tip cards, our tips one per line (Compact), or every runner (Field). */
  tipsView: 'tipsView',
  /** 'number' | 'odds' | 'barrier' | 'settle' - the order of runners in the Field view. */
  fieldSort: 'fieldSort',
  /** 'shown' | 'dismissed' - the Field view's announcement on the tips page; cleared for good once dismissed or Field is picked. */
  fieldNews: 'fieldNews',
  /** 'classic' | 'new' - which design a signed-in member has picked. */
  uiVersion: 'uiVersion',
  /** 'shown' | 'dismissed' - the new-look invitation on the classic pages. */
  newLookBanner: 'newLookBanner',
  /** 'shown' | 'dismissed' - the "New" callout above the chat button; cleared for good once dismissed or the chat is opened. */
  chatNews: 'chatNews',
} as const;
